/* eslint-disable react/no-did-update-set-state */
import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { ReactSortable } from 'react-sortablejs'
import Icon from '../../elementals/icon'
import classnames from 'classnames/bind'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const itemArrayPropType = PropTypes.arrayOf(
  PropTypes.shape({
    text: PropTypes.node,
    value: PropTypes.string,
  }).isRequired
)

/**
 * CustomContainer allows us to semi control what the list container looks like...
 * We pass the elmId through via id in this case.
 * ReactSortable requires it to be a forwardRef.
 */
const CustomContainer = forwardRef(({ children, className, id }, ref) => (
  <div ref={ref} className={className} data-elm-id={id}>
    {children}
  </div>
))

CustomContainer.propTypes = {
  children: PropTypes.any,
  className: PropTypes.string,
  id: PropTypes.string,
}

const defaultOptionRenderer = option =>
  option && (
    <div
      key={option.value}
      data-id={option.value}
      data-elm-id='item'
      className={cx('item')}
    >
      <Icon
        data-elm-id='move_item'
        name='up-down'
        iconStyle='solid'
        color='grayMedium'
        hoverColor='error'
        className={cx('icon-button')}
      />
      {option.text}
    </div>
  )

/**
 * Simple sortable component.
 * @param {array} list - children elements of list to be sortable (manually)
 * @param {boolean} canSort - if false, user cannot reorder items in list
 * @param {function} onChange - change handler which is passed an array of values
 * @param {string} elmId - element id
 */
const SortableList = ({
  list = [],
  canSort,
  elmId,
  onChange,
  captureListRef,
  optionRenderer = defaultOptionRenderer,
  showAsGrid = false,
}) => (
  <ReactSortable
    className={cx(
      'block-list',
      { grid: showAsGrid },
      { empty: list.length === 0 }
    )}
    id={elmId}
    tag={CustomContainer}
    ref={captureListRef}
    sort={canSort}
    animation={150}
    group={{
      name: 'shared',
      pull: true,
      put: true,
    }}
    setList={onChange}
    list={list}
  >
    {list.map(optionRenderer)}
  </ReactSortable>
)

SortableList.propTypes = {
  list: itemArrayPropType,
  canSort: PropTypes.bool.isRequired,
  elmId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  captureListRef: PropTypes.func,
  optionRenderer: PropTypes.func,
  showAsGrid: PropTypes.bool,
}

export default SortableList
